var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { convertToNumber3 } from "flavr-common";
import { Vector3 } from "flavr-math";
import { findMediaScreenModels } from "@messages/mediaScreen";
import { FlyToDestinationMessage } from "@messages/locomotion/LocomotionMessages";
import { RequestCollisionShapeAABBMessage, ResponseCollisionShapeAABBMessage } from "@messages/selection";
import { busService } from "@modules/bus";
import { modelPool } from "@modules/model";
import { peerService } from "@modules/multiplayer/peer";
import { roomService } from "@modules/room/RoomService";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { ConnectionStatus } from "./connection-status/ConnectionStatus.component";
import { useRemoteVideoStreamsStore } from "../../../../../../store/remote-video-streams.store";
import { useParticipantsStore } from "../../../../../../store/participants.store";
import { MenuButton } from "../../../../../../components/menu-button/MenuButton.component";
import { useRoomsParticipantStore } from "../../../../../../store/session-rooms.store";
const ttPos = "bottom-left";
export function ParticipantListItem({ user, selected, speaking, showStatus, onClick }) {
    const remoteVideoStreams = useRemoteVideoStreamsStore();
    const users = useParticipantsStore();
    const { t } = useTranslation();
    const roomsParticipantList = useRoomsParticipantStore((state) => state.roomsParticipantList);
    const [subRoomsInCurrentRoom, setSubRoomsInCurrentRoom] = useState([]);
    const currentRoom = modelPool.getRoomOrThrow(roomService.currentRoomModelUID);
    useEffect(() => {
        roomService
            .getSubRoomsOfCurrentRoom()
            .then((value) => {
            setSubRoomsInCurrentRoom(value !== null && value !== void 0 ? value : []);
        })
            .catch((error) => {
            console.error(error);
        });
    }, []);
    const getSignedUserDistanceToMyself = (user) => {
        const participantRoom = getParticipantRoom(user);
        if (participantRoom !== undefined) {
            return participantRoom.getRoomDepth() - currentRoom.getRoomDepth();
        }
        return 0;
    };
    const getParticipantRoom = (user) => {
        const participant = roomsParticipantList.find((sessionMemberInRoom) => sessionMemberInRoom.clientId === user.clientId);
        if (participant === undefined)
            return undefined;
        return modelPool.getRoom(participant.roomUid);
    };
    const isUserInMyRoom = (user) => {
        const participantRoom = getParticipantRoom(user);
        return (participantRoom === null || participantRoom === void 0 ? void 0 : participantRoom.getUID()) === currentRoom.getUID();
    };
    const isUserInChildRoom = (user) => {
        var _a;
        const participantRoom = getParticipantRoom(user);
        return ((_a = participantRoom === null || participantRoom === void 0 ? void 0 : participantRoom.getRoom()) === null || _a === void 0 ? void 0 : _a.getUID()) === currentRoom.getUID();
    };
    const isUserInParentRoom = (user) => {
        var _a;
        const participantRoom = getParticipantRoom(user);
        return ((_a = currentRoom.getRoom()) === null || _a === void 0 ? void 0 : _a.getUID()) === (participantRoom === null || participantRoom === void 0 ? void 0 : participantRoom.getUID());
    };
    const getRoomNameOfUser = (user) => {
        var _a;
        let name = "";
        const participant = roomsParticipantList.find((sessionMemberInRoom) => sessionMemberInRoom.clientId === user.clientId);
        if (participant !== undefined) {
            const participantsRoom = modelPool.getModel(participant.roomUid);
            const allRoomModelIds = [roomService.currentRoomModelUID, ...subRoomsInCurrentRoom];
            const parentRoom = currentRoom.getRoom();
            // add the parent room to the list of allRoomModelIds
            if (parentRoom !== undefined) {
                allRoomModelIds.push(parentRoom.getUID());
            }
            // add the participants room to the list of allRoomModelIds
            if (participantsRoom !== undefined) {
                allRoomModelIds.push(participantsRoom.getUID());
            }
            const roomModelUID = allRoomModelIds.find((modelUID) => modelUID === participant.roomUid);
            if (roomModelUID !== undefined) {
                name = modelPool.getRoomOrThrow(roomModelUID).getName();
            }
            else {
                name = currentRoom.getName();
            }
        }
        if (name === "rest")
            name = (_a = localStorage.getItem("session")) !== null && _a !== void 0 ? _a : "";
        return " " + name;
    };
    const userAvatar = useMemo(() => {
        var _a, _b;
        const stream = (_a = remoteVideoStreams.remoteCameraStreams.find((s) => s.clientId === user.clientId)) === null || _a === void 0 ? void 0 : _a.stream;
        if (stream) {
            return (React.createElement("video", { id: `shared-screen-video-${user.clientId}`, autoPlay: true, playsInline: true, muted: true, ref: (ref) => {
                    if (ref)
                        ref.srcObject = stream;
                } }));
        }
        else {
            return React.createElement("img", { src: ((_b = user.avatar) === null || _b === void 0 ? void 0 : _b.imageUrl) || "img/avatars/defaultUserImage.jpeg", alt: "Avatar" });
        }
    }, [user, remoteVideoStreams]);
    const isUserMuted = (user) => {
        return !!users.mutedUsers.find((u) => u === user);
    };
    const isStreamOpenedByThisUser = () => {
        var _a;
        return ((_a = remoteVideoStreams.openedScreenStream) === null || _a === void 0 ? void 0 : _a.clientId) === user.clientId;
    };
    const onToggleUserMuted = (event, user) => {
        event.stopPropagation();
        const mutedUsers = users.mutedUsers;
        if (isUserMuted(user)) {
            mutedUsers.splice(mutedUsers.indexOf(user), 1);
        }
        else {
            mutedUsers.push(user);
        }
        users.setMutedUsers(mutedUsers);
    };
    const onJumpToUser = (event, user) => __awaiter(this, void 0, void 0, function* () {
        event.stopPropagation();
        const peerMgr = peerService.peerMgr;
        if (!peerMgr) {
            console.error("Peer Manager was not found!");
            return;
        }
        const peerAvatar = peerMgr.peerAvatars.get(user.clientId);
        if (!peerAvatar) {
            console.error("Peer Connection was not found. Client id is: " + user.clientId);
            return;
        }
        const connection = busService.connection("ParticipantListItem");
        const jumpTarget = yield resolveJumpTarget(peerAvatar.currentRoomUID, peerAvatar.position, connection);
        if (jumpTarget === undefined) {
            console.warn(`Could not resolve a valid jump target for peer ${user.clientId}`);
            return;
        }
        const peerAvatarScale = Math.max(peerAvatar.scale.x, peerAvatar.scale.y, peerAvatar.scale.z);
        // Jump to user
        connection.send(new FlyToDestinationMessage(convertToNumber3(jumpTarget.jumpPosition), {
            lookAt: convertToNumber3(jumpTarget.lookAtPosition),
            scaleFactor: peerAvatarScale,
            durationInSeconds: 0
        }));
    });
    const resolveJumpTarget = (peerRoomUID, peerAvatarPosition, connection) => __awaiter(this, void 0, void 0, function* () {
        if (peerRoomUID === undefined)
            return undefined;
        const currentRoom = modelPool.getRoom(roomService.currentRoomModelUID);
        const peerRoom = modelPool.getRoom(peerRoomUID);
        if (currentRoom === undefined || peerRoom === undefined)
            return undefined;
        if (peerRoom.getUID() === currentRoom.getUID()) {
            return {
                jumpPosition: peerAvatarPosition.clone(),
                lookAtPosition: peerAvatarPosition.clone()
            };
        }
        if (!peerRoom.isDescendantOf(currentRoom)) {
            return undefined;
        }
        let targetRoom = peerRoom;
        let parentRoom = targetRoom.getRoom();
        while (parentRoom !== undefined && parentRoom.getUID() !== currentRoom.getUID()) {
            targetRoom = parentRoom;
            parentRoom = targetRoom.getRoom();
        }
        if ((parentRoom === null || parentRoom === void 0 ? void 0 : parentRoom.getUID()) !== currentRoom.getUID()) {
            return undefined;
        }
        const targetRoomPosition = targetRoom.getWorldPosition();
        return {
            jumpPosition: yield resolveSubRoomJumpTarget(targetRoom.getUID(), targetRoomPosition, peerAvatarPosition, connection),
            lookAtPosition: targetRoomPosition.clone()
        };
    });
    const resolveSubRoomJumpTarget = (targetRoomUID, targetRoomPosition, peerAvatarPosition, connection) => __awaiter(this, void 0, void 0, function* () {
        const currentViewerPosition = localPlayerContainer.cameraWorldPosition.clone();
        const approachDirection = currentViewerPosition.sub(targetRoomPosition);
        if (approachDirection.lengthSq() < 1e-6) {
            approachDirection.copy(targetRoomPosition).sub(peerAvatarPosition);
        }
        if (approachDirection.lengthSq() < 1e-6) {
            approachDirection.set(1, 0, 0);
        }
        approachDirection.normalize();
        try {
            const response = yield connection.askByMessageType(new RequestCollisionShapeAABBMessage(targetRoomUID, true), ResponseCollisionShapeAABBMessage);
            const min = new Vector3().fromArray(response.min);
            const max = new Vector3().fromArray(response.max);
            const center = min.clone().add(max).multiplyScalar(0.5);
            const halfExtents = max.clone().sub(min).multiplyScalar(0.5);
            const distanceToSurface = Math.min(approachDirection.x !== 0 ? Math.abs(halfExtents.x / approachDirection.x) : Number.POSITIVE_INFINITY, approachDirection.y !== 0 ? Math.abs(halfExtents.y / approachDirection.y) : Number.POSITIVE_INFINITY, approachDirection.z !== 0 ? Math.abs(halfExtents.z / approachDirection.z) : Number.POSITIVE_INFINITY);
            if (Number.isFinite(distanceToSurface)) {
                return center.addScaledVector(approachDirection, distanceToSurface * 3);
            }
        }
        catch (error) {
            console.warn(`Could not resolve collision bounds for room ${targetRoomUID}`, error);
        }
        return targetRoomPosition.clone().addScaledVector(approachDirection, 1);
    });
    const ttCloseRemoteScreenshare = () => t("userlist:button-close-remote-screenshare", "Close the screen shared by {{user}}", { user: user.customUsername });
    const createSharedScreenButton = () => {
        const screenStreams = remoteVideoStreams.remoteScreenStreams.filter((s) => s.clientId === user.clientId);
        if (screenStreams.length <= 0) {
            console.error("No screen stream found for user " + user.clientId);
            return undefined;
        }
        const isStreamOpened = isStreamOpenedByThisUser();
        if (screenStreams.length === 1 || isStreamOpened) {
            const onToggleSharedScreen = (event) => {
                event.stopPropagation();
                if (isStreamOpenedByThisUser()) {
                    remoteVideoStreams.setOpenedScreenStream(undefined);
                }
                else {
                    remoteVideoStreams.setOpenedScreenStream(remoteVideoStreams.remoteScreenStreams.find((s) => s.clientId === user.clientId));
                }
            };
            return (React.createElement("button", { className: "icon-button-small", onClick: onToggleSharedScreen, "data-tooltip": isStreamOpened
                    ? ttCloseRemoteScreenshare()
                    : t("userlist:button-open-remote-screenshare", "View the screen shared by {{user}}", { user: user.customUsername }), "data-tooltip-position": ttPos },
                React.createElement(FontAwesomeIcon, { icon: ["fas", isStreamOpened ? "times" : "external-link-alt"] })));
        }
        else {
            // Multiple streams but none opened
            const models = findMediaScreenModels().filter(m => screenStreams.find(s => s.surfaceUid === m.getUID()));
            const options = models.map((m) => {
                return {
                    text: m.getName(),
                    onClick: () => {
                        remoteVideoStreams.setOpenedScreenStream(screenStreams.find((s) => s.surfaceUid === m.getUID()));
                    }
                };
            });
            return (React.createElement(MenuButton, { icon: "external-link-alt", key: user.clientId, closeOnSelect: true, menuPlacement: "bottom-left", menuTitle: t("userlist:button-remote-screenshares-for-user", "Select screen to view"), size: "small", options: options, tooltip: isStreamOpened
                    ? ttCloseRemoteScreenshare()
                    : t("userlist:button-open-remote-screenshare-multiple", "View one of the screens shared by {{user}}", { user: user.customUsername }), tooltipPosition: ttPos }));
        }
    };
    const userIsInMyRoom = isUserInMyRoom(user);
    const userIsInChildRoom = isUserInChildRoom(user);
    const userIsInParentRoom = isUserInParentRoom(user);
    const signedUserDistanceToMyself = getSignedUserDistanceToMyself(user);
    const userDistanceToMyselfGreaterThanOne = Math.abs(signedUserDistanceToMyself) > 1;
    const roomNameOfUser = getRoomNameOfUser(user);
    const participantRoom = getParticipantRoom(user);
    const userIsInDescendantRoom = participantRoom !== undefined ? currentRoom.isAncestorOf(participantRoom) : false;
    const userIsInAncestorRoom = participantRoom !== undefined ? participantRoom.isAncestorOf(currentRoom) : false;
    const isUserInAnotherRoomBranch = !userIsInParentRoom && !userIsInChildRoom && !userIsInMyRoom && !userIsInDescendantRoom && !userIsInAncestorRoom;
    return (React.createElement("section", { key: user.clientId, className: `side-panel-list-item ${selected ? "side-panel-list-item-selected" : ""}`, onClick: onClick },
        React.createElement("div", { className: "side-panel-list-item-upper" },
            React.createElement("div", { className: `side-panel-list-item-avatar
          ${speaking ? "side-panel-list-item-avatar-speaking" : ""}`, "data-tooltip": isUserMuted(user) ? t("userlist:user-muted-tooltip", "You have muted this user") : undefined, "data-tooltip-position": ttPos },
                userAvatar,
                isUserMuted(user) ||
                    (userDistanceToMyselfGreaterThanOne && (React.createElement(FontAwesomeIcon, { icon: ["fas", "microphone-slash"], className: "side-panel-list-item-avatar-mic" })))),
            React.createElement("div", { className: "side-panel-list-item-upper-text" },
                React.createElement("span", { "data-tooltip": user.customUsername, "data-tooltip-position": ttPos },
                    React.createElement("div", { className: userDistanceToMyselfGreaterThanOne ? "side-panel-list-item-name-far-away-in-hierarchy" : "" }, user.isLoading ? t("userlist:user-state-loading", "Loading...") : user.customUsername),
                    !user.isLoading && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "side-panel-list-footer-spacer" }),
                        userIsInMyRoom && React.createElement(FontAwesomeIcon, { icon: ["fas", "people-roof"] }),
                        userIsInChildRoom && React.createElement(FontAwesomeIcon, { icon: ["fas", "chevron-down"] }),
                        userIsInParentRoom && React.createElement(FontAwesomeIcon, { icon: ["fas", "chevron-up"] }),
                        isUserInAnotherRoomBranch && React.createElement(FontAwesomeIcon, { icon: ["fas", "code-branch"] }),
                        !userIsInParentRoom && userIsInAncestorRoom && signedUserDistanceToMyself < -1 && (React.createElement(FontAwesomeIcon, { className: userDistanceToMyselfGreaterThanOne ? "side-panel-list-item-name-far-away-in-hierarchy" : "", icon: ["fas", "angles-up"] })),
                        !userIsInChildRoom && userIsInDescendantRoom && signedUserDistanceToMyself > 1 && (React.createElement(FontAwesomeIcon, { className: userDistanceToMyselfGreaterThanOne ? "side-panel-list-item-name-far-away-in-hierarchy" : "", icon: ["fas", "angles-down"] })),
                        React.createElement("span", { className: userDistanceToMyselfGreaterThanOne ? "side-panel-list-item-name-far-away-in-hierarchy" : "" },
                            " ",
                            roomNameOfUser)))),
                remoteVideoStreams.remoteScreenStreams.find((s) => s.clientId === user.clientId) && (React.createElement("div", { className: "side-panel-list-item-upper-text-sharing" },
                    React.createElement("span", null, t("userlist:user-state-sharing", "Currently sharing")),
                    createSharedScreenButton())))),
        selected && !user.isLoading && (React.createElement(React.Fragment, null,
            showStatus === true && React.createElement(ConnectionStatus, { user: user }),
            React.createElement("div", { className: "side-panel-list-item-lower" },
                !userDistanceToMyselfGreaterThanOne && (React.createElement("button", { className: `side-panel-list-item-mute-button ${isUserMuted(user) ? "highlight-button" : ""}`, onClick: (event) => {
                        onToggleUserMuted(event, user);
                    }, "data-tooltip": isUserMuted(user) ? t("userlist:button-unmute-user-tooltip", "Unmute this user") : t("userlist:button-mute-user-tooltip", "Mute this user"), "data-tooltip-position": ttPos }, isUserMuted(user) ? t("userlist:button-unmute-user", "Unmute") : t("userlist:button-mute-user", "Mute"))),
                signedUserDistanceToMyself === 0 && (React.createElement("button", { className: `side-panel-list-item-mute-button`, onClick: (event) => {
                        onJumpToUser(event, user);
                    }, "data-tooltip": t("userlist:button-jump-to-user-tooltip", "Jump to User"), "data-tooltip-position": ttPos }, t("userlist:button-jump-to-user", "Jump"))))))));
}
